import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
  ConditionalActionDto,
  ConditionalActionMatcherDto,
  EditActionDto,
  MatchActionDto,
} from "../../api/types";
import { SortableList, moveItem, useSortableIds } from "../SortableList";
import {
  ExpenseTypePickerField,
  GroupPickerField,
  SourcePickerField,
} from "../EntityPickerField";
import { ActionEditor } from "./ActionEditor";
import { MATCHER_LABELS } from "./constants";
import { RetiredFieldChip } from "./RetiredFieldChip";

/**
 * The fields a match may dispatch on — every one of them single-valued. A
 * match that dispatches on a retired field still renders, read-only, so it
 * can be fixed (see `offeredFields`).
 */
const DISPATCH_FIELDS = [
  "source",
  "group",
  "expenseType",
] as const satisfies readonly ConditionalActionMatcherDto["field"][];

export type DispatchField = (typeof DISPATCH_FIELDS)[number] | "retired";

const offeredFields = (current: DispatchField): readonly DispatchField[] =>
  current === "retired" ? [...DISPATCH_FIELDS, current] : DISPATCH_FIELDS;

export type MatchActionEditorProps = {
  value: MatchActionDto;
  onChange: (a: EditActionDto) => void;
  onRemove: () => void;
  isMobile: boolean;
  templateInputs?: readonly string[] | null;
};

const emptyArm = (field: DispatchField): ConditionalActionDto => ({
  // Every arm shares the match's field, a retired one included — the save
  // refuses it either way, and a mixed match would be a different error.
  condition: (field === "retired"
    ? { field, value: "account" }
    : { field, value: [] }) as ConditionalActionMatcherDto,
  action: { field: "description", value: "" },
});

/**
 * A dispatch over one field, with no default arm.
 *
 * Reads as a table of "when the source is one of these, do that". The arms are
 * the same conditional actions used elsewhere, minus their per-arm field
 * dropdown — the field belongs to the match, because every arm shares it — and
 * they can be dragged, since the first arm that matches is the one that runs.
 *
 * What it *doesn't* have is an "otherwise": a transaction none of the arms name
 * is reported against the transaction rather than quietly skipped, which is
 * the reason to reach for this instead of a stack of conditionals.
 */
export const MatchActionEditor = ({
  value,
  onChange,
  onRemove,
  isMobile,
  templateInputs = null,
}: MatchActionEditorProps) => {
  const ids = useSortableIds(value.arms.length);
  // Every arm dispatches on the same field, so the first one speaks for all.
  const field = (value.arms[0]?.condition.field ?? "source") as DispatchField;

  const setArms = (arms: ConditionalActionDto[]) =>
    onChange({ field: "match", value: { arms } });

  const setArm = (index: number, arm: ConditionalActionDto) =>
    setArms(value.arms.map((a, i) => (i === index ? arm : a)));

  const changeField = (next: DispatchField) => {
    // An id of one kind names nothing in another registry, so the arms keep
    // their actions and lose their picks.
    setArms(
      value.arms.map((arm) => ({
        ...arm,
        condition: { field: next, value: [] } as ConditionalActionMatcherDto,
      })),
    );
  };

  const addArm = () => {
    ids.append();
    setArms([...value.arms, emptyArm(field)]);
  };

  const removeArm = (index: number) => {
    ids.remove(index);
    setArms(value.arms.filter((_, i) => i !== index));
  };

  const moveArm = (from: number, to: number) => {
    ids.move(from, to);
    setArms(moveItem(value.arms, from, to));
  };

  const picker = (index: number, arm: ConditionalActionDto) => {
    const set = (condition: ConditionalActionMatcherDto) =>
      setArm(index, { ...arm, condition });
    switch (arm.condition.field) {
      case "source":
        return (
          <SourcePickerField
            size="small"
            multiple
            label="Is one of"
            value={arm.condition.value}
            onChange={(v) => set({ field: "source", value: v })}
          />
        );
      case "retired":
        return <RetiredFieldChip field={arm.condition.value} />;
      case "group":
        return (
          <GroupPickerField
            size="small"
            multiple
            label="Is one of"
            value={arm.condition.value}
            onChange={(v) => set({ field: "group", value: v })}
          />
        );
      case "expenseType":
        return (
          <ExpenseTypePickerField
            size="small"
            multiple
            label="Is one of"
            value={arm.condition.value}
            onChange={(v) => set({ field: "expenseType", value: v })}
          />
        );
      // Refused at save time, and not offered above — an arm can only get here
      // by way of a body written straight to the API.
      case "category":
        return (
          <Typography variant="caption" color="error">
            A match cannot dispatch on categories.
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Typography variant="subtitle2">Match on</Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={field}
              onChange={(e) => changeField(e.target.value as DispatchField)}
            >
              {offeredFields(field).map((f) => (
                <MenuItem key={f} value={f}>
                  {MATCHER_LABELS[f]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <IconButton size="small" onClick={onRemove} aria-label="remove match">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        The first arm that matches wins. There is no default: a transaction no
        arm names is reported on the transaction instead.
      </Typography>

      <Box sx={{ mt: 1 }}>
        <SortableList ids={ids.ids} itemLabel="arm" onMove={moveArm}>
          {(index, handle) => {
            const arm = value.arms[index];
            return (
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start", width: "100%" }}
              >
                <Box sx={{ pt: 0.5 }}>{handle}</Box>
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={isMobile ? 1 : 2}
                  sx={{
                    alignItems: isMobile ? "stretch" : "flex-start",
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>{picker(index, arm)}</Box>
                  <Typography sx={{ fontWeight: "bold", mt: 1 }}>→</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActionEditor
                      action={arm.action}
                      onChange={(action) => setArm(index, { ...arm, action })}
                      onRemove={() => removeArm(index)}
                      isMobile={isMobile}
                      isNested
                      templateInputs={templateInputs}
                    />
                  </Box>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => removeArm(index)}
                  aria-label={`remove arm ${index + 1}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          }}
        </SortableList>
      </Box>

      <Button
        startIcon={<AddIcon />}
        onClick={addArm}
        sx={{ mt: 1 }}
        size="small"
      >
        Add Arm
      </Button>
    </Box>
  );
};
